import { gql } from "graphql-tag";

/**
 * Retry wrapper for fetch. Used in urql client setup (Root.tsx, AuthProvider.tsx).
 * Retries queries on network errors and transient responses with exponential backoff.
 */
const RETRYABLE_STATUS_CODES = new Set([408, 429, 500, 502, 503, 504]);

interface RetryOptions {
	/** Maximum number of retries (default: 2) */
	maxRetries?: number;
	/** Base delay in ms, doubles on each retry (default: 500) */
	baseDelay?: number;
}

type FetchFn = (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;

/** Wrap fetch with automatic retry for transient failures (network errors, 5xx). */
export function withRetry(
	baseFetch: FetchFn,
	{ maxRetries = 2, baseDelay = 500 }: RetryOptions = {},
): FetchFn {
	return async (input, init) => {
		const signal = getEffectiveSignal(input, init);
		throwIfAborted(signal);
		const canRetry = isSelectedQuery(init?.body);
		let lastError: Error | null = null;

		for (let attempt = 0; attempt <= maxRetries; attempt++) {
			try {
				throwIfAborted(signal);
				const response = await baseFetch(input, init);
				throwIfAborted(signal);

				// Retry on transient server errors
				if (canRetry && RETRYABLE_STATUS_CODES.has(response.status) && attempt < maxRetries) {
					await discardResponse(response);
					throwIfAborted(signal);
					await sleep(baseDelay * Math.pow(2, attempt), signal);
					continue;
				}

				return response;
			} catch (error) {
				if (signal?.aborted) {
					throw signal.reason;
				}
				if (isAbortError(error)) {
					throw error;
				}

				lastError = error instanceof Error ? error : new Error(String(error));

				// Retry on network errors
				if (canRetry && attempt < maxRetries) {
					await sleep(baseDelay * Math.pow(2, attempt), signal);
					continue;
				}

				throw lastError;
			}
		}

		throw lastError ?? new Error("Fetch failed after retries");
	};
}

function isSelectedQuery(body: BodyInit | null | undefined): boolean {
	if (typeof body !== "string") {
		return false;
	}

	try {
		const payload: unknown = JSON.parse(body);
		if (!payload || typeof payload !== "object") {
			return false;
		}

		const { query, operationName } = payload as { query?: unknown; operationName?: unknown };
		if (typeof query !== "string") {
			return false;
		}

		const operations = gql(query).definitions.filter(
			(definition) => definition.kind === "OperationDefinition",
		);
		if (operationName === undefined) {
			return operations.length === 1 && operations[0]?.operation === "query";
		}
		if (typeof operationName !== "string") {
			return false;
		}

		const selected = operations.find((operation) => operation.name?.value === operationName);
		return selected?.operation === "query";
	} catch {
		return false;
	}
}

function getEffectiveSignal(input: RequestInfo | URL, init?: RequestInit): AbortSignal | undefined {
	if (init?.signal !== undefined) {
		return init.signal ?? undefined;
	}
	if (typeof Request !== "undefined" && input instanceof Request) {
		return input.signal;
	}
	return undefined;
}

function throwIfAborted(signal: AbortSignal | undefined): void {
	if (signal?.aborted) {
		throw signal.reason;
	}
}

function isAbortError(error: unknown): boolean {
	return error instanceof Error && error.name === "AbortError";
}

async function discardResponse(response: Response): Promise<void> {
	try {
		await response.body?.cancel();
	} catch {
		// A failed cleanup must not change retry behavior.
	}
}

function sleep(ms: number, signal: AbortSignal | undefined): Promise<void> {
	return new Promise((resolve, reject) => {
		if (signal?.aborted) {
			reject(signal.reason);
			return;
		}

		const cleanup = () => {
			clearTimeout(timer);
			signal?.removeEventListener("abort", onAbort);
		};
		const finish = () => {
			cleanup();
			resolve();
		};
		const onAbort = () => {
			cleanup();
			reject(signal?.reason);
		};
		const timer = setTimeout(finish, ms);
		signal?.addEventListener("abort", onAbort, { once: true });
	});
}
